import { ref } from "vue";
import IntercomApi from "@/experiences/intercom/api/intercom";
const MESSENGER_USED_STORAGE_KEY = "intercom:messengerUsed";
// Intercom's JS API has no boot-failure callback - `ready` only ever fires on success, with no
// built-in timeout - so a stuck boot (rejected JWT, can't reach Intercom, etc.) needs our own clock.
const BOOT_TIMEOUT_MS = 10000;
// Module-level state: there's only ever one real Intercom Messenger on a page, so this is shared by
// every <intercom-beacon> trigger instance rather than kept per-component - otherwise each trigger
// would boot (and pay for) its own JWT, and each would go stale the moment another trigger opened it.
const isOpen = ref(false);
const isBooted = ref(false);
const isLoading = ref(false);
const apiBaseForRegion = (region) => {
    switch (region) {
        case "eu":
            return "https://api-iam.eu.intercom.io";
        case "au":
            return "https://api-iam.au.intercom.io";
        default:
            return "https://api-iam.intercom.io";
    }
};
// Official Intercom install snippet: installs a queueing `window.Intercom` stub and
// lazy-loads the real widget script, so calls made before it loads are replayed once it's ready.
// Resolves once the script has loaded (or immediately, if it's already installed); rejects if the
// script itself fails to load (network down, ad blocker, CSP, etc.).
const installIntercomScript = (appId) => {
    return new Promise((resolve, reject) => {
        if (typeof window.Intercom === "function") {
            window.Intercom("reattach_activator");
            resolve();
            return;
        }
        const stub = function (...args) {
            stub.q.push(args);
        };
        stub.q = [];
        window.Intercom = stub;
        const script = document.createElement("script");
        script.type = "text/javascript";
        script.async = true;
        script.src = `https://widget.intercom.io/widget/${appId}`;
        script.onload = () => resolve();
        script.onerror = () => {
            if (window.Intercom === stub)
                window.Intercom = undefined;
            script.remove();
            reject(new Error("Failed to load the Intercom widget script"));
        };
        const firstScript = document.getElementsByTagName("script")[0];
        firstScript.parentNode?.insertBefore(script, firstScript);
    });
};
// Races Intercom's `ready` event (fires only on a successful boot) against our own timeout, since
// Intercom never fires anything on failure - a rejected JWT or an unreachable API just leaves `ready`
// silent forever.
const waitForMessengerReady = () => {
    return new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
            reject(new Error("Timed out waiting for the Intercom Messenger to boot"));
        }, BOOT_TIMEOUT_MS);
        window.Intercom?.("ready", () => {
            clearTimeout(timeoutId);
            resolve();
        });
    });
};
const bootMessenger = async (context) => {
    const { token, appId, region, styleId, locale } = await IntercomApi.getToken();
    await installIntercomScript(appId);
    window.Intercom?.("boot", {
        app_id: appId,
        api_base: apiBaseForRegion(region),
        intercom_user_jwt: token,
        hide_default_launcher: true,
        UR_LiveActivity: context.isLiveActivity ?? false,
        UR_HelpDeskOnly: context.helpdeskOnly ?? false,
        ...(context.studyAlias ? { UR_SiteStudy: context.studyAlias } : {}),
        ...(context.activityAlias ? { UR_SiteActivity: context.activityAlias } : {}),
        ...(context.primaryColor ? { background_color: context.primaryColor, link_color: context.primaryColor } : {}),
        // Exclude for now due to bug in Intercom beacon.
        // Some buttons are missing the background color making them invisible.
        //...(context.secondaryColor ? {action_color: context.secondaryColor} : {}),
        ...(styleId ? { messenger_style_id: styleId } : {}),
        ...(locale ? { language_override: locale } : {})
    });
    window.Intercom?.("onShow", () => {
        isOpen.value = true;
        sessionStorage.setItem(MESSENGER_USED_STORAGE_KEY, "true");
    });
    window.Intercom?.("onHide", () => {
        isOpen.value = false;
    });
    await waitForMessengerReady();
    isBooted.value = true;
};
let bootPromise = null;
const ensureBooted = (context) => {
    if (isBooted.value)
        return Promise.resolve(true);
    if (bootPromise)
        return bootPromise;
    isLoading.value = true;
    bootPromise = bootMessenger(context)
        .then(() => true)
        .catch((e) => {
        console.error("Failed to start the Intercom messenger", e);
        return false;
    })
        .finally(() => {
        isLoading.value = false;
        bootPromise = null;
    });
    return bootPromise;
};
// Returns false only when this call attempted (and failed) to boot, so the caller can decide whether
// to surface its own error state. A no-op while another trigger's boot is already in flight isn't a failure.
const toggleMessenger = async (context) => {
    if (isLoading.value)
        return true;
    if (!(await ensureBooted(context)))
        return false;
    if (isOpen.value) {
        window.Intercom?.("hide");
    }
    else if (context.helpdeskOnly) {
        window.Intercom?.("showSpace", "help");
    }
    else {
        window.Intercom?.("show");
    }
    return true;
};
const showMessengerArticle = async (context, articleId) => {
    if (!(await ensureBooted(context)))
        return false;
    window.Intercom?.("showArticle", articleId);
    return true;
};
const showMessengerConversation = async (context, message) => {
    if (!(await ensureBooted(context)))
        return false;
    if (message) {
        window.Intercom?.("showNewMessage", message);
    }
    else {
        window.Intercom?.("showNewMessage");
    }
    return true;
};
// Boots quietly (no error surfaced to the caller) if the Messenger was opened earlier this browser
// session, so it keeps receiving new messages/unread counts across page loads without reopening it.
const bootIfUsedThisSession = (context) => {
    if (sessionStorage.getItem(MESSENGER_USED_STORAGE_KEY) === "true") {
        ensureBooted(context);
    }
};
export const useIntercomMessenger = () => ({
    isOpen,
    isLoading,
    toggleMessenger,
    showMessengerArticle,
    showMessengerConversation,
    bootIfUsedThisSession
});
